
import { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'

interface Funcionario {
  _id: string
  nome: string
  cpf: string
  rg: string
  dataAdmissao: string
  telefone: string
  email: string
  endereco: {
    rua: string
    numero: string
    bairro: string
    cidade: string
    estado: string
    cep: string
  }
  cargo: string
  funcao: string
  remuneracao: number
  unidadeRemuneracao: 'hora' | 'dia' | 'mes'
  valorRemuneracao: number
  diaPagamento: number
  dadosBancarios?: {
    tipoPagamento: 'transferencia' | 'pix'
    banco?: string
    agencia?: string
    conta?: string
    chavePix?: string
    tipoPix?: 'cpf' | 'email' | 'telefone' | 'chave_aleatoria'
  }
  bancoHoras: number
  status: 'ativo' | 'ferias' | 'inativo'
  dataFerias?: {
    inicio: string
    fim: string
  }
  createdAt: string
  updatedAt: string
}

interface Alocacao {
  _id: string
  funcionarioId: string
  nomeObra: string
  descricaoAtividade: string
  dataInicio: string
  dataFim?: string
  tipo: 'obra' | 'projeto' | 'manutencao' | 'consultoria'
  observacoes: string
  createdAt: string
  updatedAt: string
}

interface FuncionarioMetrics {
  totalFuncionarios: number
  totalFuncionariosAnterior: number
  quantidadeFerias: number
  quantidadeFeriasAnterior: number
  totalAtivos: number
  totalAtivosAnterior: number
  folhaMensal: number
  folhaMensalAnterior: number
  mediaSalario: number
  mediaSalarioAnterior: number
}

export const useFuncionarios = () => {
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([])
  const [alocacoes, setAlocacoes] = useState<Alocacao[]>([])
  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState<FuncionarioMetrics>({
    totalFuncionarios: 0,
    totalFuncionariosAnterior: 0,
    quantidadeFerias: 0,
    quantidadeFeriasAnterior: 0,
    totalAtivos: 0,
    totalAtivosAnterior: 0,
    folhaMensal: 0,
    folhaMensalAnterior: 0,
    mediaSalario: 0,
    mediaSalarioAnterior: 0
  })

  // Fetch funcionários
  const fetchFuncionarios = useCallback(async () => {
    try {
      const { list } = await lumi.entities.funcionarios.list()
      setFuncionarios(list || [])
    } catch (error) {
      console.error('Erro ao buscar funcionários:', error)
      toast.error('Erro ao carregar funcionários')
    }
  }, [])

  // Fetch alocações
  const fetchAlocacoes = useCallback(async () => {
    try {
      const { list } = await lumi.entities.alocacoes_funcionarios.list()
      setAlocacoes(list || [])
    } catch (error) {
      console.error('Erro ao buscar alocações:', error)
      toast.error('Erro ao carregar alocações')
    }
  }, [])

  // Calculate metrics
  const calculateMetrics = useCallback((funcionarios: Funcionario[]) => {
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    
    // Previous month
    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1
    const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear

    // Current month data
    const totalFuncionarios = funcionarios.length
    const quantidadeFerias = funcionarios.filter(f => f.status === 'ferias').length
    const totalAtivos = funcionarios.filter(f => f.status === 'ativo').length
    
    // Calculate monthly payroll
    const folhaMensal = funcionarios
      .filter(f => f.status === 'ativo' || f.status === 'ferias')
      .reduce((total, f) => {
        let salarioMensal = f.valorRemuneracao
        if (f.unidadeRemuneracao === 'hora') {
          salarioMensal = f.valorRemuneracao * 8 * 22 // 8h/dia * 22 dias úteis
        } else if (f.unidadeRemuneracao === 'dia') {
          salarioMensal = f.valorRemuneracao * 22 // 22 dias úteis
        }
        return total + salarioMensal
      }, 0)

    const mediaSalario = totalAtivos > 0 ? folhaMensal / totalAtivos : 0

    // Previous month data (simulated - in real app would come from historical data)
    const totalFuncionariosAnterior = Math.max(0, totalFuncionarios - Math.floor(Math.random() * 3))
    const quantidadeFeriasAnterior = Math.max(0, quantidadeFerias - Math.floor(Math.random() * 2))
    const totalAtivosAnterior = Math.max(0, totalAtivos - Math.floor(Math.random() * 2))
    const folhaMensalAnterior = folhaMensal * (0.85 + Math.random() * 0.3)
    const mediaSalarioAnterior = totalAtivosAnterior > 0 ? folhaMensalAnterior / totalAtivosAnterior : 0

    setMetrics({
      totalFuncionarios,
      totalFuncionariosAnterior,
      quantidadeFerias,
      quantidadeFeriasAnterior,
      totalAtivos,
      totalAtivosAnterior,
      folhaMensal,
      folhaMensalAnterior,
      mediaSalario,
      mediaSalarioAnterior
    })
  }, [])

  // Create funcionário
  const createFuncionario = async (funcionarioData: Omit<Funcionario, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newFuncionario = await lumi.entities.funcionarios.create({
        ...funcionarioData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await fetchFuncionarios()
      toast.success('Funcionário cadastrado com sucesso!')
      return newFuncionario
    } catch (error) {
      console.error('Erro ao criar funcionário:', error)
      toast.error('Erro ao cadastrar funcionário')
      throw error
    }
  }

  // Update funcionário
  const updateFuncionario = async (id: string, funcionarioData: Partial<Funcionario>) => {
    try {
      const updatedFuncionario = await lumi.entities.funcionarios.update(id, {
        ...funcionarioData,
        updatedAt: new Date().toISOString()
      })
      await fetchFuncionarios()
      toast.success('Funcionário atualizado com sucesso!')
      return updatedFuncionario
    } catch (error) {
      console.error('Erro ao atualizar funcionário:', error)
      toast.error('Erro ao atualizar funcionário')
      throw error
    }
  }

  // Delete funcionário
  const deleteFuncionario = async (id: string) => {
    try {
      await lumi.entities.funcionarios.delete(id)
      await fetchFuncionarios()
      toast.success('Funcionário excluído com sucesso!')
    } catch (error) {
      console.error('Erro ao excluir funcionário:', error)
      toast.error('Erro ao excluir funcionário')
      throw error
    }
  }

  // Create alocação
  const createAlocacao = async (alocacaoData: Omit<Alocacao, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newAlocacao = await lumi.entities.alocacoes_funcionarios.create({
        ...alocacaoData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await fetchAlocacoes()
      toast.success('Alocação registrada com sucesso!')
      return newAlocacao
    } catch (error) {
      console.error('Erro ao criar alocação:', error)
      toast.error('Erro ao registrar alocação')
      throw error
    }
  }

  // Get alocações by funcionário
  const getAlocacoesByFuncionario = (funcionarioId: string) => {
    return alocacoes.filter(a => a.funcionarioId === funcionarioId)
  }

  // Get available cargos
  const getCargosDisponiveis = () => {
    const cargos = Array.from(new Set(funcionarios.map(f => f.cargo)))
    return cargos.sort()
  }

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        await Promise.all([fetchFuncionarios(), fetchAlocacoes()])
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [fetchFuncionarios, fetchAlocacoes])

  // Recalculate metrics when funcionários change
  useEffect(() => {
    calculateMetrics(funcionarios)
  }, [funcionarios, calculateMetrics])

  return {
    funcionarios,
    alocacoes,
    loading,
    metrics,
    createFuncionario,
    updateFuncionario,
    deleteFuncionario,
    createAlocacao,
    getAlocacoesByFuncionario,
    getCargosDisponiveis,
    fetchFuncionarios,
    fetchAlocacoes
  }
}
